import { useState } from 'react';
import { useSeoWeekly, useBulkUpdatePositions } from '../hooks/useSeoKeywords';
import StatCard from '../components/ui/StatCard';
import ProjectContextBadge from '../components/layout/ProjectContextBadge';

const CAT_COLORS = {
  primary: 'bg-teal/10 text-teal',
  secondary: 'bg-amber-50 text-amber-600',
  brand: 'bg-purple-50 text-purple-600',
};

const CAT_LABELS = {
  primary: 'Primär',
  secondary: 'Sekundär',
  brand: 'Brand',
};

function CheckpointCard({ cp }) {
  const isDue = cp.status === 'due';
  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${
      isDue ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white'
    }`}>
      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-bold shrink-0 ${
        isDue ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-400'
      }`}>
        {isDue ? '!' : '○'}
      </span>
      <div className="flex-1">
        <div className={`text-[13px] font-medium ${isDue ? 'text-amber-800' : 'text-gray-600'}`}>
          {cp.label}
        </div>
        <div className="text-[11px] text-gray-400">
          {new Date(cp.date).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
        </div>
      </div>
      <span className={`text-[11px] font-medium px-2.5 py-1 rounded-full ${
        isDue ? 'bg-amber-200 text-amber-800' : 'bg-gray-100 text-gray-500'
      }`}>
        {isDue ? 'Fällig' : 'Ausstehend'}
      </span>
    </div>
  );
}

function TargetCard({ t }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="text-[11px] text-gray-400 font-medium mb-1">{t.metric}</div>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[14px] text-gray-400">{t.before}</span>
        <span className="text-gray-300">→</span>
        <span className="text-[16px] font-bold text-teal">{t.target}</span>
      </div>
      <div className="text-[11px] text-gray-400">{t.description}</div>
    </div>
  );
}

export default function SeoWeekly() {
  const { data, isLoading } = useSeoWeekly();
  const [expandedWeek, setExpandedWeek] = useState(null);

  const weeks = data?.weeks || [];
  const checkpoints = data?.checkpoints || [];
  const targets = data?.targets || [];
  const currentStats = data?.current_stats || {};

  return (
    <div>
      {/* Header */}
      <div className="mb-5">
        <ProjectContextBadge />
        <h1 className="text-[23px] font-bold text-gray-900">Wöchentliches Controlling</h1>
        <p className="text-[13px] text-gray-500 mt-0.5">SEO-Performance des aktiven Projekts im Wochenverlauf</p>
      </div>

      {/* Current Stats */}
      <div className="grid grid-cols-4 gap-3.5 mb-5">
        <StatCard icon="🔑" value={currentStats.total_keywords || 0} label="Keywords" sub="Aktiv getrackt" />
        <StatCard icon="📍" value={currentStats.avg_position || '—'} label="Ø Position" sub="Aktueller Durchschnitt" />
        <StatCard icon="↑" value={currentStats.improved || 0} label="Verbessert" sub="Seit Start" />
        <StatCard icon="✓" value={currentStats.on_target || 0} label="Im Ziel" sub="Target erreicht" />
      </div>

      {/* Checkpoints */}
      <div className="mb-5">
        <h2 className="text-[15px] font-bold text-gray-900 mb-3">Prüfzeitpunkte</h2>
        <div className="space-y-2">
          {checkpoints.map((cp, i) => (
            <CheckpointCard key={i} cp={cp} />
          ))}
        </div>
      </div>

      {/* Targets */}
      <div className="mb-5">
        <h2 className="text-[15px] font-bold text-gray-900 mb-3">Erwartete Effekte</h2>
        <div className="grid grid-cols-3 gap-3.5">
          {targets.map((t, i) => (
            <TargetCard key={i} t={t} />
          ))}
        </div>
      </div>

      {/* Weekly History */}
      <div className="mb-3">
        <h2 className="text-[15px] font-bold text-gray-900">Wochenberichte</h2>
        <p className="text-[12px] text-gray-400 mt-0.5">Ranking-Entwicklung nach Kalenderwoche</p>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-8 text-center text-gray-400 text-[13px]">
          Lade Daten...
        </div>
      ) : weeks.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-8 text-center text-gray-400 text-[13px]">
          Noch keine Ranking-Daten erfasst. Nutze den Ranking Checker um Positionen einzutragen.
        </div>
      ) : (
        <div className="space-y-3">
          {weeks.map((week) => {
            const isExpanded = expandedWeek === week.week;
            const avgPos = week.entries.length > 0
              ? (week.entries.reduce((s, e) => s + e.position, 0) / week.entries.length).toFixed(1)
              : '—';
            const improved = week.entries.filter((e) => e.position_start && e.position < e.position_start).length;

            return (
              <div key={week.week} className="bg-white rounded-xl shadow-sm border border-black/[.07] overflow-hidden">
                <button
                  onClick={() => setExpandedWeek(isExpanded ? null : week.week)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50/50 transition-colors text-left"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-[15px] font-bold text-gray-900">{week.label}</span>
                    <span className="text-[12px] text-gray-400">{week.entries.length} Keywords erfasst</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-[12px] text-gray-500">Ø {avgPos}</span>
                    {improved > 0 && (
                      <span className="text-[12px] text-green-600 font-medium">↑ {improved} verbessert</span>
                    )}
                    <span className={`text-[14px] text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                      ▾
                    </span>
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-gray-100">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left text-[11px] text-gray-500 font-medium px-5 py-2.5">Keyword</th>
                          <th className="text-left text-[11px] text-gray-500 font-medium px-4 py-2.5">Kategorie</th>
                          <th className="text-center text-[11px] text-gray-500 font-medium px-4 py-2.5">Start</th>
                          <th className="text-center text-[11px] text-gray-500 font-medium px-4 py-2.5">Position</th>
                          <th className="text-center text-[11px] text-gray-500 font-medium px-4 py-2.5">Veränderung</th>
                          <th className="text-center text-[11px] text-gray-500 font-medium px-4 py-2.5">Ziel</th>
                        </tr>
                      </thead>
                      <tbody>
                        {week.entries.map((entry, i) => {
                          const change = entry.position_start ? entry.position_start - entry.position : null;
                          return (
                            <tr key={`${entry.keyword_id}-${i}`} className={i < week.entries.length - 1 ? 'border-b border-gray-50' : ''}>
                              <td className="px-5 py-2.5 text-[13px] font-medium text-gray-900">{entry.keyword}</td>
                              <td className="px-4 py-2.5">
                                <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-full ${CAT_COLORS[entry.category]}`}>
                                  {CAT_LABELS[entry.category]}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-center text-[12px] text-gray-500">
                                {entry.position_start !== null ? Number(entry.position_start).toFixed(1) : '—'}
                              </td>
                              <td className="px-4 py-2.5 text-center text-[14px] font-semibold text-gray-900">
                                {Number(entry.position).toFixed(1)}
                              </td>
                              <td className="px-4 py-2.5 text-center">
                                {change !== null ? (
                                  <span className={`text-[12px] font-medium ${change > 0 ? 'text-green-600' : change < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                                    {change > 0 ? '↑' : change < 0 ? '↓' : '±'} {Math.abs(change).toFixed(1)}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-[12px]">—</span>
                                )}
                              </td>
                              <td className="px-4 py-2.5 text-center text-[12px] text-gray-500">
                                {entry.target_position || '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
